// @ts-nocheck
import { forwardRef } from 'react';
import {
  Box,
  Stack,
  Text,
  Button,
  Badge,
} from '@mantine/core';
import { IconEye } from '@tabler/icons-react';
import { Card } from '../../../ui';
import { FadeIn } from '../../../animations';
import AlertCard from './AlertCard';
import type { AlertListProps, Alert } from '../types';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

/**
 * AlertList - Multiple alerts list
 *
 * Displays a list of alerts with optional filtering and "view all" action.
 */
const AlertList = forwardRef<HTMLDivElement, AlertListProps>(
  (
    {
      alerts,
      maxItems = 5,
      onDismiss,
      onViewAll,
      variant = 'default',
      showCategory = false,
      filterBySeverity,
      className,
    },
    ref
  ) => {
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    // Filter alerts by severity if specified
    let filteredAlerts = alerts;
    if (filterBySeverity && filterBySeverity.length > 0) {
      filteredAlerts = alerts.filter((alert) =>
        filterBySeverity.includes(alert.severity)
      );
    }

    // Sort by timestamp (newest first)
    const sortedAlerts = [...filteredAlerts].sort(
      (a, b) => b.timestamp.getTime() - a.timestamp.getTime()
    );

    // Limit to maxItems
    const displayAlerts = sortedAlerts.slice(0, maxItems);
    const hasMore = sortedAlerts.length > maxItems;

    // Count by severity
    const severityCounts = alerts.reduce((acc, alert) => {
      acc[alert.severity] = (acc[alert.severity] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return (
      <FadeIn>
        <Card ref={ref} withBorder p="lg" className={className}>
          <Stack gap="md">
            {/* Header */}
            <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text size="lg" fw={600} style={accessibilityStyles}>
                Alerts
              </Text>

              <Box style={{ display: 'flex', gap: 6 }}>
                {Object.entries(severityCounts).map(([severity, count]) => {
                  const colors = {
                    error: 'red',
                    warning: 'yellow',
                    info: 'blue',
                    success: 'green',
                  };
                  return (
                    <Badge
                      key={severity}
                      color={colors[severity] || 'gray'}
                      variant="light"
                      size="sm"
                    >
                      {count}
                    </Badge>
                  );
                })}
              </Box>
            </Box>

            {/* Alert list */}
            {displayAlerts.length === 0 ? (
              <Text size="sm" c="dimmed" ta="center" py="xl" style={accessibilityStyles}>
                No alerts to display
              </Text>
            ) : (
              <Stack gap="sm">
                {displayAlerts.map((alert) => (
                  <AlertCard
                    key={alert.id}
                    alert={alert}
                    onDismiss={onDismiss}
                    variant={variant}
                    showTimestamp
                  />
                ))}
              </Stack>
            )}

            {/* View all button */}
            {(hasMore || onViewAll) && (
              <Button
                variant="light"
                fullWidth
                leftSection={<IconEye size={16} />}
                onClick={onViewAll}
              >
                {hasMore ? `View all ${sortedAlerts.length} alerts` : 'View all alerts'}
              </Button>
            )}
          </Stack>
        </Card>
      </FadeIn>
    );
  }
);

AlertList.displayName = 'AlertList';

export default AlertList;
